import type { RouterConfig } from '@nuxt/schema'

// Custom scroll behavior. Replaces Nuxt's default because the album filter
// needs a navigation to land at the TOP OF THE GALLERY: with the default,
// any component-side scroll races Nuxt's own scroll-to-top (which runs at
// an unpredictable moment after page:finish) and loses. Here the router
// performs exactly ONE scroll per navigation — the value we return.
type ScrollResult = Awaited<ReturnType<NonNullable<RouterConfig['scrollBehavior']>>>

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    const nuxtApp = useNuxtApp()

    return new Promise<ScrollResult>((resolve) => {
      let settled = false

      const resolveOnce = (position: ScrollResult) => {
        if (settled) return
        settled = true
        resolve(position)
      }

      // Album switch (flag set by album-filter right before router.push):
      // wait for the gallery wrapper to exist — it lives inside
      // <client-only>, which renders a tick after the page mounts — then
      // land exactly on its album filter. Falls back to page top if it
      // never appears (e.g. gallery disabled).
      const resolveAtGallery = (attempt = 0) => {
        // `settled` also guards the IMPERATIVE scrolls below, not just the
        // promise resolution: the 1500ms backstop timer is never cancelled,
        // and without this guard its second decide() re-scrolled the layout
        // to the gallery 1.5s after the switch — yanking a user who had
        // already scrolled on.
        if (settled) return
        const gallery = document.querySelector<HTMLElement>('.event-page__gallery')
        if (gallery) {
          // Collapsed header (banner instead of the full-screen hero): the
          // gallery already starts at the page top — land at 0 so the
          // banner + chips stay in view instead of being clipped off by
          // a filter landing.
          if (document.querySelector('.event-header-banner')) {
            const collapsedLayout = gallery.closest<HTMLElement>('.event-layout')
            collapsedLayout?.scrollTo({ top: 0, behavior: 'instant' })
            resolveOnce(false)
            return
          }
          // Land on the FILTER, not on the wrapper's top: the guestbook
          // band is the wrapper's first child, so a wrapper landing pushed
          // the chips a band's height DOWN the screen — the chip you just
          // tapped moved out from under your finger. offsetTop (not a
          // bounding rect) because the filter is sticky: a rect would
          // report where it is pinned, not where it sits in the page. Only
          // usable while both offsets are measured from the same
          // offsetParent.
          const filter = gallery.querySelector<HTMLElement>('.album-filter')
          const top =
            filter && filter.offsetParent === gallery.offsetParent
              ? filter.offsetTop
              : gallery.offsetTop
          // The event pages scroll inside .event-layout (position:absolute,
          // overflow-y:scroll) — the WINDOW never scrolls there, so a
          // returned window position would be a silent no-op. Scroll the
          // container imperatively instead; gallery.offsetTop is already
          // container-relative (.event-layout is its offsetParent).
          const layout = gallery.closest<HTMLElement>('.event-layout')
          if (layout) {
            // Only ever bring the filter INTO view. Picking a second album
            // from the already-pinned strip must not move the page at all:
            // landing "at the filter" from below scrolls the gallery back
            // up under the chip the guest just tapped.
            if (layout.scrollTop < top) {
              layout.scrollTo({ top, behavior: 'instant' })
            }
            resolveOnce(false)
            return
          }
          resolveOnce(window.scrollY < top ? { top, behavior: 'instant' } : false)
          return
        }
        if (attempt < 30) {
          requestAnimationFrame(() => resolveAtGallery(attempt + 1))
          return
        }
        resolveOnce(false)
      }

      const decide = () => {
        if (settled) return
        const albumSwitch = useState<number | null>('album-switch-scroll')
        // NOT consumed on read: scrollBehavior fires more than once per
        // navigation on this stack, and a consumed-once flag let the
        // second invocation scroll back to the top. The timestamp keeps
        // every invocation within the window landing at the gallery and
        // expires on its own.
        const isAlbumSwitch =
          typeof albumSwitch.value === 'number' && Date.now() - albumSwitch.value < 3000
        if (isAlbumSwitch) {
          resolveAtGallery()
          return
        }
        if (savedPosition) {
          resolveOnce(savedPosition)
          return
        }
        if (to.hash) {
          resolveOnce({ el: to.hash })
          return
        }
        if (to.path === from.path) {
          // Query-only change (e.g. ?imageModal=) — stay put.
          resolveOnce({})
          return
        }
        resolveOnce({ top: 0 })
      }

      // Decide after the new page has finished rendering, like Nuxt's own
      // behavior; the timeout guards against page:finish not firing (e.g.
      // aborted navigation) so the router promise can never hang.
      nuxtApp.hooks.hookOnce('page:finish', () => {
        setTimeout(decide, 0)
      })
      setTimeout(decide, 1500)
    })
  },
}
